import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { QUALITY_PRESETS, type PublishStatus, type StreamInfo, type WHIPStats } from '../lib/types';
import { WHIPClient } from '../lib/whip';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, ApiError, type StreamViewer, type SecretMessage, type EventSchedule } from '../lib/api-client';
import { formatDateTime, formatScheduleDropdown } from '../lib/date-utils';
import { ROUTES } from '../lib/constants';

import ChatPanel from '../components/ChatPanel';
import { useStreamViewers } from '../hooks/useStreamViewers';
import { usePartyCount } from '../hooks/usePartyCount';
import { useSecretMessages } from '../hooks/useSecretMessages';
import { useMessageTranslation } from '../hooks/useMessageTranslation';
import StreamerHeader from '../components/StreamerHeader';
import StreamerMyPageModal from '../components/StreamerMyPageModal';
import { useEkycApplication } from '../hooks/useEkycApplication';

export function resolveNextViewerSelection(
    currentSelection: string | null,
    viewers: StreamViewer[],
): string | null {
    if (viewers.length === 0) {
        return null;
    }

    if (currentSelection && viewers.some((viewer) => viewer.username === currentSelection)) {
        return currentSelection;
    }

    return null;
}

const MODERATION_POLL_INTERVAL_MS = 10_000;

export default function PublishOme() {
    const { user, logout } = useAuth();
    const {
        ekycStatus,
        isLoading: isEkycLoading,
        error: ekycError,
        refreshStatus,
    } = useEkycApplication();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const scheduleIdFromUrl = searchParams.get('schedule');
    const { i18n } = useTranslation();

    // Force Japanese language for streamer pages
    useEffect(() => {
        if (i18n.language !== 'ja') {
            i18n.changeLanguage('ja');
        }
    }, [i18n]);

    const [quality, setQuality] = useState('1080p');
    const [status, setStatus] = useState<PublishStatus>('stopped');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [streamInfo, setStreamInfo] = useState<StreamInfo>({
        resolution: '-',
        connectionState: 'new',
    });
    const [stats, setStats] = useState<WHIPStats>({
        bitrate: 0,
        fps: 0,
    });

    // Streamer status tracking (修正指示その３)
    const [streamerStatus, setStreamerStatus] = useState<'waiting' | 'party' | 'two_shot'>('waiting');

    // Stats tracking
    const [broadcastStartTime, setBroadcastStartTime] = useState<Date | null>(null);
    const [broadcastDuration, setBroadcastDuration] = useState(0);

    // Stop confirmation modal
    const [showStopModal, setShowStopModal] = useState(false);

    // Broadcast status (live/paused) and loading state
    const [broadcastStatus, setBroadcastStatus] = useState<'live' | 'paused'>('live');
    const [isPauseResumeLoading, setIsPauseResumeLoading] = useState(false);

    // Event schedule selection
    const [mySchedules, setMySchedules] = useState<EventSchedule[]>([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
    const [loadingSchedules, setLoadingSchedules] = useState(false);

    // Secret message popup notification
    const [secretMessagePopup, setSecretMessagePopup] = useState<SecretMessage | null>(null);
    const [popupTranslatedText, setPopupTranslatedText] = useState<string | null>(null);
    const prevSecretMessagesCountRef = useRef<number>(0);
    const seenSecretMessageIdsRef = useRef<Set<number>>(new Set());
    const isInitialSecretMessageFetchRef = useRef<boolean>(true);  // 初回フェッチかどうか
    const hasSeenLoadingTrueRef = useRef<boolean>(false);  // isLoadingがtrueになったことがあるか

    // Translation hook for popup fallback translation
    const { translateForDisplay, needsTranslation, userLang } = useMessageTranslation();

    // MyPage modal (for use during broadcast)
    const [showMyPageModal, setShowMyPageModal] = useState(false);

    // Moderation: force-stopped (publish blocked) or suspended by the operator
    const [moderationBlock, setModerationBlock] = useState<'publish_blocked' | 'suspended' | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);
    const localStreamRef = useRef<MediaStream | null>(null);
    const whipClientRef = useRef<WHIPClient | null>(null);
    const isStoppingRef = useRef(false);
    const streamerStatusPollingRef = useRef<number | null>(null);
    const durationIntervalRef = useRef<number | null>(null);


    // stream_key is now the schedule_id (UUID)
    // This ensures chat history persists across re-logins
    // The streamKey is derived from selectedScheduleId
    const streamKey = selectedScheduleId || '';

    // chatStreamKey is same as streamKey (schedule_id)
    const chatStreamKey = streamKey || null;

    // Use refs to access latest values in cleanup without triggering re-renders
    const statusRef = useRef(status);
    const userRef = useRef(user);

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // リロード/ログアウト/ブラウザバック時は一時中断にする（完全停止ではなく）
    // これにより、1分以内に再接続すれば視聴者側はシームレスに映像が再開する
    const suspendStreamIfNeeded = useCallback(
        async (options?: { keepalive?: boolean }) => {
            if (isStoppingRef.current) {
                return;
            }

            if (statusRef.current !== 'publishing' || !userRef.current) {
                return;
            }

            isStoppingRef.current = true;
            try {
                await apiClient.stopStream({ stream_key: streamKey }, options);
                console.log('[API] Stream suspended for navigation/logout');
            } catch (apiError) {
                console.error('[API] Failed to suspend stream:', apiError);
            } finally {
                isStoppingRef.current = false;
            }
        },
        [streamKey]
    );

    const isPublishing = status === 'publishing';
    const { viewers: activeViewers } = useStreamViewers({ streamKey, isEnabled: isPublishing, pollIntervalMs: 2000 });

    // URLからスケジュールIDが指定された場合は初期選択
    useEffect(() => {
        if (scheduleIdFromUrl && !selectedScheduleId) {
            setSelectedScheduleId(scheduleIdFromUrl);  // schedule_id is now a string (UUID)
        }
    }, [scheduleIdFromUrl, selectedScheduleId]);

    // Fetch event schedules for this streamer
    useEffect(() => {
        const fetchMySchedules = async () => {
            if (!user || user.role !== 'STREAMER') return;

            setLoadingSchedules(true);
            try {
                // Use upcoming_only to get both 'scheduled' and 'live' statuses
                // This ensures streamers can see schedules that are currently live
                // (e.g., if status wasn't properly updated when stream stopped)
                const response = await apiClient.listEventSchedules({
                    streamer_id: user.id,
                    upcoming_only: true,
                    limit: 20,
                });
                setMySchedules(response.schedules);
                // Auto-select from URL parameter only if the schedule exists
                // Never auto-select a different schedule than what was requested
                if (response.schedules.length > 0 && !selectedScheduleId) {
                    if (scheduleIdFromUrl) {
                        // URLのスケジュールIDがリストにあるか確認 (schedule_id is now a string UUID)
                        const found = response.schedules.find(s => s.id === scheduleIdFromUrl);
                        if (found) {
                            setSelectedScheduleId(scheduleIdFromUrl);
                        }
                        // 見つからない場合は何も選択しない（勝手に別のIDを選択しない）
                    } else {
                        // URLパラメータがない場合のみ、最初のスケジュールを自動選択
                        setSelectedScheduleId(response.schedules[0].id);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch schedules:', error);
            } finally {
                setLoadingSchedules(false);
            }
        };

        fetchMySchedules();
    }, [user]);

    // Note: chatStreamKey is now derived from selectedScheduleId (= streamKey)
    // No need for separate useEffect - they are the same value

    // Use usePartyCount for stable viewer counts (based on is_in_party/is_peeking flags, not last_seen timeout)
    const { partyCount, peekCount } = usePartyCount(isPublishing ? streamKey : null);
    const partyViewers = activeViewers.filter(
        viewer => viewer.user_status === 'party' || viewer.user_status === 'two_shot'
    );

    // Viewer counts from party-count API (more stable than viewer list)
    const liveViewerCount = partyCount;
    const peekViewerCount = peekCount;
    const totalViewerCount = partyCount + peekCount;

    // Secret messages for streamer (to detect new messages and show popup)
    const { messages: secretMessages, isLoading: isSecretMessagesLoading } = useSecretMessages(streamKey, isPublishing, true);

    // Detect new secret messages and show popup
    useEffect(() => {
        console.log('[SecretMessage] Effect triggered - isPublishing:', isPublishing, 'streamKey:', streamKey, 'messagesCount:', secretMessages.length, 'isLoading:', isSecretMessagesLoading);

        if (!isPublishing) {
            return;
        }

        // isLoadingがtrueになったことを追跡
        // これにより、useState(false)の初期値とフェッチ完了後のfalseを区別できる
        if (isSecretMessagesLoading) {
            hasSeenLoadingTrueRef.current = true;
            console.log('[SecretMessage] Loading started...');
            return;
        }

        // isLoadingがまだtrueになっていない場合は待機
        // （useStateの初期値falseとフェッチ完了後のfalseを区別）
        if (!hasSeenLoadingTrueRef.current) {
            console.log('[SecretMessage] Waiting for loading to start...');
            return;
        }

        // 初回フェッチ完了後、既存メッセージを全て「既読」としてマークし、ポップアップは表示しない
        // これにより再ログイン時に既存メッセージがポップアップ表示されるのを防ぐ
        if (isInitialSecretMessageFetchRef.current) {
            isInitialSecretMessageFetchRef.current = false;
            // 既存メッセージを全て既読としてマーク
            secretMessages.forEach((msg) => {
                seenSecretMessageIdsRef.current.add(msg.id);
            });
            console.log('[SecretMessage] Initial sync complete - marked', secretMessages.length, 'existing messages as seen');
            return;
        }

        if (secretMessages.length === 0) {
            console.log('[SecretMessage] No messages to process');
            return;
        }

        // Find new messages that we haven't seen before
        const newMessages = secretMessages.filter(
            (msg) => !seenSecretMessageIdsRef.current.has(msg.id)
        );
        console.log('[SecretMessage] New messages detected:', newMessages.length, 'total seen:', seenSecretMessageIdsRef.current.size);

        if (newMessages.length > 0) {
            // Mark all current messages as seen
            secretMessages.forEach((msg) => {
                seenSecretMessageIdsRef.current.add(msg.id);
            });

            // Show popup for the newest message (last in array)
            const newestMessage = newMessages[newMessages.length - 1];
            console.log('[SecretMessage] Showing popup for message:', newestMessage.id, 'text:', newestMessage.message_text?.substring(0, 30), 'original_lang:', newestMessage.original_lang);
            setSecretMessagePopup(newestMessage);
        }
    }, [secretMessages, isPublishing, isSecretMessagesLoading, streamKey]);

    // Auto-hide secret message popup after 10 seconds (separate effect to avoid polling interference)
    useEffect(() => {
        if (!secretMessagePopup) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setSecretMessagePopup(null);
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, [secretMessagePopup?.id]);

    // Trigger fallback translation for popup when backend translation is not available
    useEffect(() => {
        if (!secretMessagePopup) {
            setPopupTranslatedText(null);
            return;
        }

        const displayText = secretMessagePopup.display_text;
        const messageText = secretMessagePopup.message_text;
        const originalLang = secretMessagePopup.original_lang;

        // If backend already provided translation, use it
        if (displayText && displayText !== messageText) {
            setPopupTranslatedText(displayText);
            return;
        }

        // If message is in user's language, no translation needed
        if (originalLang === userLang) {
            setPopupTranslatedText(messageText);
            return;
        }

        // Trigger fallback translation if message is in different language
        if (needsTranslation && originalLang && originalLang !== userLang) {
            translateForDisplay(secretMessagePopup.id, messageText, originalLang)
                .then((translated) => {
                    if (translated) {
                        setPopupTranslatedText(translated);
                    } else {
                        setPopupTranslatedText(messageText);
                    }
                })
                .catch(() => {
                    setPopupTranslatedText(messageText);
                });
        } else {
            // No translation available, use original
            setPopupTranslatedText(messageText);
        }
    }, [secretMessagePopup, translateForDisplay, needsTranslation, userLang]);

    // Reset seen messages when stream stops
    useEffect(() => {
        if (!isPublishing) {
            seenSecretMessageIdsRef.current.clear();
            prevSecretMessagesCountRef.current = 0;
            isInitialSecretMessageFetchRef.current = true;  // 次回配信開始時のために初回フラグをリセット
            hasSeenLoadingTrueRef.current = false;  // 次回配信開始時のためにロード追跡フラグをリセット
        }
    }, [isPublishing]);

    // Broadcast duration timer
    useEffect(() => {
        if (isPublishing && broadcastStartTime) {
            durationIntervalRef.current = window.setInterval(() => {
                const now = new Date();
                const diff = Math.floor((now.getTime() - broadcastStartTime.getTime()) / 1000 / 60);
                setBroadcastDuration(diff);
            }, 1000);
        } else {
            if (durationIntervalRef.current) {
                window.clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
        }

        return () => {
            if (durationIntervalRef.current) {
                window.clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
        };
    }, [isPublishing, broadcastStartTime]);

    // Check the operator's moderation state; a force-stop or suspension ends the
    // local broadcast and replaces the studio with a blocking screen (requirement 1.8).
    const checkModerationStatus = useCallback(async () => {
        if (userRef.current?.role !== 'STREAMER') return;
        try {
            const moderation = await apiClient.getMyModerationStatus();
            const block = moderation.suspended ? 'suspended' : moderation.publish_blocked ? 'publish_blocked' : null;
            setModerationBlock(block);
            if (block && statusRef.current === 'publishing') {
                cleanup();
                setStatus('stopped');
                setBroadcastStartTime(null);
            }
        } catch (err) {
            console.debug('Failed to check moderation status:', err);
        }
        // cleanup only touches refs, so the callback can stay stable
    }, []);

    useEffect(() => {
        if (user?.role !== 'STREAMER') return undefined;
        void checkModerationStatus();
        const timer = window.setInterval(() => { void checkModerationStatus(); }, MODERATION_POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [user?.role, checkModerationStatus]);

    // Poll streamer status every 2 seconds when publishing (修正指示その３)
    useEffect(() => {
        if (!streamKey || status !== 'publishing') {
            if (streamerStatusPollingRef.current) {
                window.clearInterval(streamerStatusPollingRef.current);
                streamerStatusPollingRef.current = null;
            }
            return;
        }

        const pollStreamerStatus = async () => {
            try {
                const data = await apiClient.getStreamerStatus(streamKey);
                setStreamerStatus(data.status);
            } catch (err) {
                // Silently ignore errors - stream may not be registered yet
                console.debug('Failed to poll streamer status:', err);
            }
        };

        // Initial fetch
        pollStreamerStatus();

        // Start polling
        streamerStatusPollingRef.current = window.setInterval(pollStreamerStatus, 2000);

        return () => {
            if (streamerStatusPollingRef.current) {
                window.clearInterval(streamerStatusPollingRef.current);
                streamerStatusPollingRef.current = null;
            }
        };
    }, [streamKey, status]);

    useEffect(() => {
        // Cleanup on page unload/reload (only set up once on mount)
        const handleBeforeUnload = () => {
            void suspendStreamIfNeeded({ keepalive: true });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            void suspendStreamIfNeeded();
            cleanup();
        };
    }, [streamKey, suspendStreamIfNeeded]); // Only re-run if streamKey changes (which it shouldn't)

    useEffect(() => {
        const handlePopState = () => {
            void suspendStreamIfNeeded();
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [suspendStreamIfNeeded]);

    const startPublish = async () => {
        try {
            setStatus('starting');
            setErrorMessage(null);

            const qualityPreset = QUALITY_PRESETS[quality];

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: qualityPreset.width },
                    height: { ideal: qualityPreset.height },
                    frameRate: { ideal: qualityPreset.frameRate },
                },
                audio: {
                    channelCount: { ideal: 2 },
                    sampleRate: { ideal: 48000 },
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            console.log('[WHIP] Video+Audio stream created');
            localStreamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            const videoTrack = stream.getVideoTracks()[0];
            const settings = videoTrack.getSettings();
            setStreamInfo({
                resolution: `${settings.width}×${settings.height} @ ${settings.frameRate}fps`,
                connectionState: 'new',
            });

            const streamStartPayload = user ? {
                stream_key: streamKey,
                username: user.username,
                user_id: user.id,
                schedule_id: selectedScheduleId ?? undefined,
            } : null;

            if (streamStartPayload) {
                await apiClient.authorizePublish(streamStartPayload);
                console.log('[API] OME publish authorized');
            }

            whipClientRef.current = new WHIPClient({
                serverType: 'ome',
                streamKey,
                qualityPreset: QUALITY_PRESETS[quality],
                onConnectionStateChange: (state) => {
                    setStreamInfo(prev => ({ ...prev, connectionState: state }));
                },
                onStatsUpdate: (newStats) => {
                    setStats(newStats);
                }
            });

            await whipClientRef.current.start(stream);

            // Set status to publishing immediately after successful WHIP start
            setStatus('publishing');
            setBroadcastStartTime(new Date());
            setBroadcastDuration(0);

            // Notify backend that stream started or resumed
            if (user) {
                try {
                    // まず既存の一時中断ストリームがあるか確認
                    const response = await apiClient.getActiveStreams();
                    const existingPausedStream = response.streams.find(
                        s => s.stream_key === streamKey && s.broadcast_status === 'paused'
                    );

                    if (existingPausedStream) {
                        // 一時中断中のストリームがあればresumeStreamを呼ぶ
                        try {
                            await apiClient.resumeStream(streamKey);
                            setBroadcastStatus('live');
                            console.log('[API] Stream resumed from paused state');
                        } catch (resumeError) {
                            // resumeが失敗した場合（ストリームが既に終了している場合など）は新規開始
                            console.warn('[API] Resume failed, starting new stream:', resumeError);
                            await apiClient.startStream(streamStartPayload!);
                            console.log('[API] Stream started after resume failure');
                        }
                    } else {
                        // 新規配信の場合はstartStreamを呼ぶ
                        await apiClient.startStream(streamStartPayload!);
                        console.log('[API] Stream started notification sent');
                    }
                } catch (apiError) {
                    console.error('[API] Failed to notify stream start/resume:', apiError);
                    // Continue even if API call fails - stream is still active
                }
            }

        } catch (error) {
            console.error('[Publish] Error:', error);
            if (error instanceof ApiError && (error.code === 'PUBLISH_BLOCKED' || error.code === 'ACCOUNT_SUSPENDED')) {
                setModerationBlock(error.code === 'PUBLISH_BLOCKED' ? 'publish_blocked' : 'suspended');
                setStatus('stopped');
                cleanup();
                return;
            }
            setStatus('error');

            // Provide specific error messages for common device errors
            if (error instanceof Error) {
                if (error.name === 'NotReadableError') {
                    setErrorMessage('カメラまたはマイクが他のアプリケーションで使用中です。\n\n解決方法:\n• Zoom、Teams、Skype等のビデオ通話アプリを終了してください\n• 他のブラウザタブを閉じてください\n• ブラウザを再起動してください');
                } else if (error.name === 'NotFoundError') {
                    setErrorMessage('カメラまたはマイクが見つかりません。デバイスが接続されているか確認してください。');
                } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setErrorMessage('カメラとマイクへのアクセスが許可されていません。ブラウザの設定でアクセスを許可してください。');
                } else if (error.name === 'OverconstrainedError') {
                    setErrorMessage('選択した画質設定に対応していません。画質を下げて再試行してください。');
                } else {
                    setErrorMessage(`配信開始エラー: ${error.message}`);
                }
            } else {
                setErrorMessage('配信の開始に失敗しました。再度お試しください。');
            }

            cleanup();
        }
    };

    const handleStopClick = () => {
        setShowStopModal(true);
    };

    const handlePauseStream = async () => {
        if (isPauseResumeLoading) return;
        setIsPauseResumeLoading(true);
        try {
            console.log('[Pause] Attempting to pause stream:', streamKey);
            const response = await apiClient.pauseStream(streamKey);
            setBroadcastStatus('paused');
            console.log('[Pause] Stream paused successfully:', response.message);
        } catch (error: unknown) {
            console.error('[Pause] Failed to pause stream:', error);
            const message = error instanceof ApiError ? error.detail : '一時中断に失敗しました';
            alert(`一時中断エラー: ${message}`);
        } finally {
            setIsPauseResumeLoading(false);
        }
    };

    const handleResumeStream = async () => {
        if (isPauseResumeLoading) return;
        setIsPauseResumeLoading(true);
        try {
            console.log('[Resume] Attempting to resume stream:', streamKey);
            const response = await apiClient.resumeStream(streamKey);
            setBroadcastStatus('live');
            console.log('[Resume] Stream resumed successfully:', response.message);
        } catch (error: unknown) {
            console.error('[Resume] Failed to resume stream:', error);
            const message = error instanceof ApiError ? error.detail : '配信再開に失敗しました';
            alert(`配信再開エラー: ${message}`);
            // If stream not found (404), the stream may have been stopped - update UI
            if (error instanceof ApiError && error.status === 404) {
                setBroadcastStatus('live');
                setStatus('stopped');
            }
        } finally {
            setIsPauseResumeLoading(false);
        }
    };

    const confirmStopPublish = async () => {
        setShowStopModal(false);
        // 配信終了時は直接APIを呼び出して確実に停止させる
        let stopSuccess = false;
        const maxRetries = 3;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                await apiClient.stopStream({ stream_key: streamKey });
                console.log(`[confirmStopPublish] Stream stopped successfully (attempt ${attempt})`);
                stopSuccess = true;
                break;
            } catch (err) {
                console.error(`[confirmStopPublish] Failed to stop stream (attempt ${attempt}/${maxRetries}):`, err);
                if (attempt < maxRetries) {
                    // リトライ前に少し待機
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        }

        if (!stopSuccess) {
            setErrorMessage('配信停止に失敗しました。ページを更新してから再度お試しください。');
            // 失敗してもローカルのクリーンアップは実行
        }

        setStatus('stopped');
        setBroadcastStartTime(null);
        cleanup();
    };

    const cancelStopPublish = () => {
        setShowStopModal(false);
    };

    const cleanup = () => {
        if (whipClientRef.current) {
            whipClientRef.current.stop();
            whipClientRef.current = null;
        }

        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setStreamInfo(prev => ({ ...prev, connectionState: 'new' }));
        setStats({ bitrate: 0, fps: 0 });
    };

    const getStatusLabel = () => {
        // Show paused status if broadcast is paused
        if (broadcastStatus === 'paused') {
            return '⏸️ 一時中断中';
        }

        switch (streamerStatus) {
            case 'waiting':
                return '🟢 待機中';
            case 'party':
                return '🔴 配信中';
            case 'two_shot':
                return '💕 2ショットチャット';
            default:
                return '🟢 待機中';
        }
    };

    // コンソール移動時は一時中断にする（完全停止ではなく）
    const stopPublishForNavigation = async () => {
        await suspendStreamIfNeeded();
        setStatus('stopped');
        setBroadcastStartTime(null);
        cleanup();
    };

    if (user?.role === 'STREAMER' && isEkycLoading) {
        return (
            <div className="min-h-screen bg-gray-50 text-gray-900">
                <StreamerHeader
                    activeTab="broadcast"
                    onMyPageClick={() => navigate(ROUTES.STREAMER_MYPAGE)}
                    onStudioClick={() => navigate(ROUTES.STUDIO)}
                    onLogout={() => {
                        logout();
                        window.location.href = ROUTES.STAFF_LOGIN;
                    }}
                    username={user?.username}
                    isPublishing={false}
                />
                <div className="max-w-xl mx-auto px-4 py-24 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500 mx-auto mb-4"></div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">本人確認ステータスを確認中です</h1>
                    <p className="text-sm text-gray-600">配信画面を開く前に、eKYC本人確認の状態を確認しています。</p>
                </div>
            </div>
        );
    }

    if (user?.role === 'STREAMER' && moderationBlock) {
        return (
            <div className="min-h-screen bg-gray-50 text-gray-900">
                <StreamerHeader
                    activeTab="broadcast"
                    onMyPageClick={() => navigate(ROUTES.STREAMER_MYPAGE)}
                    onStudioClick={() => navigate(ROUTES.STUDIO)}
                    onLogout={() => {
                        logout();
                        window.location.href = ROUTES.STAFF_LOGIN;
                    }}
                    username={user?.username}
                    isPublishing={false}
                />
                <div className="max-w-xl mx-auto px-4 py-20">
                    <div className="bg-white border border-red-200 shadow-sm p-8 text-center" role="alert">
                        <h1 className="text-2xl font-bold text-red-700 mb-3">
                            {moderationBlock === 'suspended' ? 'このアカウントは利用停止中です' : '運営により配信が停止されています'}
                        </h1>
                        <p className="text-sm text-gray-600 mb-6">
                            現在は配信を開始できません。詳しくは運営にお問い合わせください。
                        </p>
                        <button
                            onClick={() => void checkModerationStatus()}
                            className="py-2.5 px-5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md font-semibold text-sm"
                        >
                            状態を再確認
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (user?.role === 'STREAMER' && ekycStatus !== 'APPROVED') {
        const isRejected = ekycStatus === 'REJECTED';
        const isExpired = ekycStatus === 'EXPIRED';
        const isProcessing = ekycStatus === 'SUBMITTED' || ekycStatus === 'API_VERIFYING' || ekycStatus === 'PENDING_REVIEW';

        return (
            <div className="min-h-screen bg-gray-50 text-gray-900">
                <StreamerHeader
                    activeTab="broadcast"
                    onMyPageClick={() => navigate(ROUTES.STREAMER_MYPAGE)}
                    onStudioClick={() => navigate(ROUTES.STUDIO)}
                    onLogout={() => {
                        logout();
                        window.location.href = ROUTES.STAFF_LOGIN;
                    }}
                    username={user?.username}
                    isPublishing={false}
                />
                <div className="max-w-xl mx-auto px-4 py-20">
                    <div className="bg-white border border-gray-200 shadow-sm p-8 text-center">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            isRejected ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'
                        }`}>
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-3">配信には本人確認が必要です</h1>
                        {ekycError ? (
                            <p className="text-sm text-red-600 mb-6">{ekycError}</p>
                        ) : isRejected ? (
                            <p className="text-sm text-gray-600 mb-6">
                                本人確認が却下されています。マイページから再度本人確認を行ってください。
                            </p>
                        ) : isExpired ? (
                            <p className="text-sm text-gray-600 mb-6">
                                本人確認がタイムアウトしています。スマートフォンでマイページから再度本人確認を行ってください。
                            </p>
                        ) : isProcessing ? (
                            <p className="text-sm text-gray-600 mb-6">
                                本人確認は審査中です。承認が完了すると配信画面を利用できます。
                            </p>
                        ) : (
                            <p className="text-sm text-gray-600 mb-6">
                                配信を開始するには、マイページで本人確認を完了してください。
                            </p>
                        )}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={() => navigate(ROUTES.STREAMER_MYPAGE)}
                                className="py-2.5 px-5 bg-pink-500 hover:bg-pink-600 text-white rounded-md font-semibold text-sm"
                            >
                                マイページで本人確認する
                            </button>
                            <button
                                onClick={() => void refreshStatus()}
                                className="py-2.5 px-5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md font-semibold text-sm"
                            >
                                ステータスを再確認
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-gray-50 text-gray-900">
                <StreamerHeader
                activeTab="broadcast"
                onMyPageClick={() => {
                    if (isPublishing) {
                        // During broadcast, show modal instead of navigating
                        setShowMyPageModal(true);
                    } else {
                        // When not broadcasting, navigate to full mypage
                        navigate(ROUTES.STREAMER_MYPAGE);
                    }
                }}
                onStudioClick={async () => {
                    await stopPublishForNavigation();
                    navigate(ROUTES.STUDIO);
                }}
                onLogout={async () => {
                    // ログアウト時は一時中断にする（完全停止ではなく）
                    // 1分以内に再ログインすれば視聴者側はシームレスに映像が再開する
                    if (user?.id) {
                        try {
                            const response = await apiClient.getActiveStreams();
                            const myStreams = response.streams.filter(s => s.user_id === user.id);
                            for (const stream of myStreams) {
                                // 接続を閉じるので60秒の再開猶予がある中断へ移す
                                await apiClient.stopStream({ stream_key: stream.stream_key });
                            }
                        } catch (err) {
                            console.error('Failed to suspend streams on logout:', err);
                        }
                    }
                    setStatus('stopped');
                    setBroadcastStartTime(null);
                    cleanup();
                    logout();
                    window.location.href = ROUTES.STAFF_LOGIN;
                }}
                statusLabel={isPublishing ? getStatusLabel() : undefined}
                username={user?.username}
                isPublishing={isPublishing}
            />

            <div className="max-w-[1600px] mx-auto px-4 py-4">
                {/* Stats Panel */}
                <div className="bg-white border border-gray-200 shadow-sm p-4 mb-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                            <div className="text-2xl font-bold text-pink-500 mb-1">
                                {totalViewerCount}
                            </div>
                            <div className="text-sm text-gray-500">総視聴者数</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                            <div className="text-2xl font-bold text-pink-500 mb-1">
                                {broadcastDuration}分
                            </div>
                            <div className="text-sm text-gray-500">配信時間</div>
                        </div>
                        {/* Schedule Time Display - Always show slots */}
                        {(() => {
                            const selectedSchedule = mySchedules.find(s => s.id === selectedScheduleId);
                            if (selectedSchedule) {
                                const startTime = new Date(selectedSchedule.scheduled_start);
                                const endTime = new Date(selectedSchedule.scheduled_end);
                                return (
                                    <>
                                        <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                                            <div className="text-xl font-bold text-pink-500 mb-1">
                                                {formatDateTime(startTime)}
                                            </div>
                                            <div className="text-sm text-gray-500">予定開始</div>
                                        </div>
                                        <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                                            <div className="text-xl font-bold text-pink-500 mb-1">
                                                {formatDateTime(endTime)}
                                            </div>
                                            <div className="text-sm text-gray-500">予定終了</div>
                                        </div>
                                    </>
                                );
                            }
                            // Show placeholder when no schedule is selected
                            return (
                                <>
                                    <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                                        <div className="text-xl font-bold text-gray-400 mb-1">
                                            --:--
                                        </div>
                                        <div className="text-sm text-gray-500">予定開始</div>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 p-4 text-center">
                                        <div className="text-xl font-bold text-gray-400 mb-1">
                                            --:--
                                        </div>
                                        <div className="text-sm text-gray-500">予定終了</div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Main Layout - Video + Sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
                    {/* Video Section */}
                    <div>
                        <div className="bg-black">
                            {/* Room Status Bar */}
                            <div className="bg-black text-white flex items-stretch justify-between pr-4 text-sm">
                                <div className={`px-3 py-2 font-semibold flex items-center ${isPublishing ? 'bg-pink-500' : 'bg-gray-500'}`}>
                                    {isPublishing ? '配信中' : 'オフライン'}
                                </div>
                                <div className="flex gap-6 items-center text-white">
                                    <div className="flex items-center gap-2">
                                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                                        </svg>
                                        <span>チャット中: <strong>{liveViewerCount}</strong>人</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>のぞき中: <strong>{peekViewerCount}</strong>人</span>
                                    </div>
                                </div>
                            </div>

                            {/* Video Preview */}
                            <div className="relative bg-black">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="w-full aspect-video bg-black"
                                />

                                {/* Camera Placeholder when not publishing */}
                                {!isPublishing && status !== 'starting' && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                                        <div className="text-6xl mb-4">📹</div>
                                        <div className="text-xl">カメラプレビュー</div>
                                        <div className="text-gray-400 text-sm mt-2">配信を開始してください</div>
                                    </div>
                                )}

                                {/* Broadcast Paused Overlay */}
                                {isPublishing && broadcastStatus === 'paused' && (
                                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <div className="text-6xl mb-4">⏸️</div>
                                            <div className="text-2xl font-bold mb-2">配信一時中断中</div>
                                            <p className="text-gray-300">視聴者には「配信一時中断中」と表示されています</p>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* Stream Info Bar */}
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600 border border-gray-200 bg-gray-50 p-3">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">解像度:</span>
                                <span>{streamInfo.resolution}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium">接続:</span>
                                <span className={`${streamInfo.connectionState === 'connected' ? 'text-green-600' : ''}`}>
                                    {streamInfo.connectionState}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium">ビットレート:</span>
                                <span>{stats.bitrate} kbps</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium">FPS:</span>
                                <span>{stats.fps}</span>
                            </div>
                        </div>

                        {/* Error Message Display */}
                        {status === 'error' && errorMessage && (
                            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="text-red-500 text-xl">⚠️</span>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-red-700 mb-2">配信開始エラー</h4>
                                        <p className="text-red-600 text-sm whitespace-pre-line">{errorMessage}</p>
                                        <button
                                            onClick={() => {
                                                setStatus('stopped');
                                                setErrorMessage(null);
                                            }}
                                            className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors"
                                        >
                                            閉じる
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Video Actions */}
                        <div className="pt-4 flex justify-center gap-3">
                            {status !== 'publishing' ? (
                                <button
                                    onClick={startPublish}
                                    disabled={status === 'starting' || status === 'error'}
                                    className="px-8 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none"
                                >
                                    {status === 'starting' ? '起動中...' : '配信開始'}
                                </button>
                            ) : (
                                <>
                                    {/* Pause/Resume Button */}
                                    {broadcastStatus === 'live' ? (
                                        <button
                                            onClick={handlePauseStream}
                                            disabled={isPauseResumeLoading}
                                            className="px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none"
                                        >
                                            {isPauseResumeLoading ? '処理中...' : '⏸️ 一時中断'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleResumeStream}
                                            disabled={isPauseResumeLoading}
                                            className="px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none"
                                        >
                                            {isPauseResumeLoading ? '処理中...' : '▶️ 配信再開'}
                                        </button>
                                    )}
                                    {/* Stop Button */}
                                    <button
                                        onClick={handleStopClick}
                                        className="px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                                    >
                                        🛑 配信終了
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Event Schedule Selection */}
                        {!isPublishing && mySchedules.length > 0 && (
                            <div className="mt-4 bg-white border border-gray-200 p-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    配信スケジュール
                                    <span className="text-xs text-gray-500 ml-2">
                                        (選択したスケジュールのみ「配信中」になります)
                                    </span>
                                </label>
                                <select
                                    value={selectedScheduleId ?? ''}
                                    onChange={(e) => setSelectedScheduleId(e.target.value || null)}
                                    className="w-full p-3 bg-white border border-gray-300 text-gray-900 focus:border-pink-500 focus:outline-none"
                                    disabled={loadingSchedules}
                                >
                                    <option value="">スケジュールを選択しない</option>
                                    {mySchedules
                                        .filter((schedule) => schedule.status !== 'ended' && schedule.status !== 'cancelled')
                                        .map((schedule) => {
                                            const statusLabel = schedule.status === 'live' ? ' 🔴配信中' :
                                                schedule.status === 'paused' ? ' ⏸️一時中断' : '';
                                            return (
                                                <option key={schedule.id} value={schedule.id}>
                                                    {formatScheduleDropdown(schedule.scheduled_start, schedule.scheduled_end)}{statusLabel}
                                                </option>
                                            );
                                        })}
                                </select>
                                {loadingSchedules && (
                                    <p className="text-gray-500 text-sm mt-1">読み込み中...</p>
                                )}
                                {selectedScheduleId && (
                                    <p className="text-pink-600 text-sm mt-2">
                                        📌 選択中のスケジュールが配信開始時に「配信中」に更新されます
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Quality Settings */}
                        {!isPublishing && (
                            <div className="mt-4 bg-white border border-gray-200 p-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">画質設定</label>
                                <select
                                    value={quality}
                                    onChange={(e) => setQuality(e.target.value)}
                                    className="w-full p-3 bg-white border border-gray-300 text-gray-900 focus:border-pink-500 focus:outline-none"
                                >
                                    <option value="360p">360p (640×360 @ 30fps)</option>
                                    <option value="480p">480p (854×480 @ 30fps)</option>
                                    <option value="720p">720p (1280×720 @ 30fps)</option>
                                    <option value="1080p">1080p (1920×1080 @ 30fps)</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="flex flex-col gap-4 min-h-[400px] lg:min-h-0">
                        {/* Active Viewers Panel */}
                        <div className="flex-none bg-white border border-gray-200 shadow-sm p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="font-semibold text-gray-900">発言ルーム</span>
                                    <span className="bg-pink-500 text-white px-2 py-0.5 text-xs font-medium">
                                        {liveViewerCount}人
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {isPublishing && partyViewers.length > 0 ? (
                                        partyViewers.map(viewer => (
                                            <div
                                                key={viewer.username}
                                                className="px-3 py-2 bg-gray-100 border border-gray-200 text-sm text-gray-900 font-medium rounded"
                                            >
                                                {viewer.username}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-gray-400 text-sm py-4 text-center w-full">
                                            {isPublishing ? '視聴者がいません' : '配信開始後に表示されます'}
                                        </div>
                                    )}
                                </div>
                            </div>

                        {/* Chat Panel */}
                        <div className="relative flex-1 min-h-[300px]">
                            <div className="lg:absolute lg:inset-0 flex flex-col h-full">
                                <div className="flex-1 min-h-0">
                            <ChatPanel
                                streamKey={chatStreamKey || streamKey}
                                scheduleId={selectedScheduleId ?? undefined}
                                viewerName={user?.username || 'Streamer'}
                                chatSendEnabled={isPublishing}
                                secretMessageSendEnabled={false}
                                anonymizeUsernames={false}
                                userRole="streamer"
                            />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stop Confirmation Modal */}
            {showStopModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg max-w-md w-[90%] shadow-2xl overflow-hidden border-2 border-pink-100">
                        <div className="bg-gradient-to-r from-pink-100 to-pink-200 px-6 py-4 flex items-center gap-3 border-b border-pink-300">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl shadow">
                                ⚠️
                            </div>
                            <div className="text-xl font-bold text-pink-900">配信を終了しますか？</div>
                        </div>
                        <div className="px-6 py-6 text-gray-600 leading-relaxed">
                            配信を終了すると、<strong className="text-pink-500">視聴中の全ての視聴者</strong>が視聴できなくなります。
                            <br /><br />
                            本当に配信を終了してもよろしいですか？
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={cancelStopPublish}
                                className="px-6 py-2.5 bg-white border border-gray-300 text-gray-600 font-semibold rounded hover:bg-gray-50 hover:border-gray-400 transition"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={confirmStopPublish}
                                className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold rounded hover:from-pink-600 hover:to-pink-700 hover:-translate-y-0.5 shadow hover:shadow-lg transition-all"
                            >
                                配信を終了
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Secret Message Popup Notification */}
            {secretMessagePopup && (
                <div className="fixed bottom-6 right-6 z-50 animate-slide-in-up">
                    <div className="bg-white rounded-lg shadow-2xl border-2 border-pink-300 max-w-sm overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-pink-500 to-pink-600 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white">
                                <span className="text-lg">💌</span>
                                <span className="font-bold">秘密メッセージ</span>
                            </div>
                            <button
                                onClick={() => setSecretMessagePopup(null)}
                                className="text-white/80 hover:text-white transition"
                                aria-label="閉じる"
                            >
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        {/* Content */}
                        <div className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-pink-600">
                                    {secretMessagePopup.user_username}
                                </span>
                                <span className="text-xs text-gray-400">
                                    {new Date(secretMessagePopup.created_at.endsWith('Z') ? secretMessagePopup.created_at : secretMessagePopup.created_at + 'Z').toLocaleTimeString('ja-JP', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                        timeZone: 'Asia/Tokyo',
                                    })}
                                </span>
                            </div>
                            <p className="text-gray-800 text-sm break-words">
                                {popupTranslatedText || secretMessagePopup.message_text}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Streamer MyPage Modal (for editing profile during broadcast) */}
                <StreamerMyPageModal
                    isOpen={showMyPageModal}
                    onClose={() => setShowMyPageModal(false)}
                />
            </div>
        </>
    );
}
